import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Associate } from '../../types';
import { useCollection } from '../../hooks/useCollection';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import ConfirmModal from '../../components/ConfirmModal';
import { useToast } from '../../hooks/useToast';

const PlusIcon: React.FC<{className?: string}> = ({className}) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
);

const AdminAssociatesPage: React.FC = () => {
    const { data: associates, setData: setAssociates, isLoading } = useCollection<Associate>('associates');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [associateToDelete, setAssociateToDelete] = useState<Associate | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { showToast } = useToast();

    const openDeleteModal = (associate: Associate) => {
        setAssociateToDelete(associate);
        setIsModalOpen(true);
    };

    const handleDelete = async () => {
        if (!associateToDelete) return;

        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, 'associates', associateToDelete.id));
            setAssociates(prev => prev.filter(a => a.id !== associateToDelete.id));
            showToast('Associate removed successfully.', { type: 'success' });
        } catch (error) {
            console.error("Failed to delete associate:", error);
            showToast("Error: Could not remove the associate.", { type: 'error' });
        } finally {
            setIsModalOpen(false);
            setAssociateToDelete(null);
            setIsDeleting(false);
        }
    };
    
    return (
        <div>
            <ConfirmModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleDelete}
                title="Remove Associate"
                description={`Are you sure you want to remove ${associateToDelete?.name}? This action cannot be undone.`}
                isConfirming={isDeleting}
            />

            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-gray-800">Manage Associates</h2>
                <Link to="/admin/associate/new" className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors">
                   <PlusIcon className="h-5 w-5 mr-2"/>
                    Add New Associate
                </Link>
            </div>
            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                {isLoading && <p className="p-6 text-center">Loading associates...</p>}
                {!isLoading && (
                    <table className="min-w-full leading-normal">
                        <thead>
                            <tr>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                                    Logo
                                </th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Slug
                                </th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                        {(associates || []).map(associate => (
                                <tr key={associate.id}>
                                    <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                                        <img 
                                            src={associate.logoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(associate.name)}&background=e5e7eb&color=4b5563`} 
                                            alt={associate.name} 
                                            className="w-12 h-12 rounded-full object-contain bg-gray-100 p-1"
                                        />
                                    </td>
                                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                        <p className="text-gray-900 whitespace-no-wrap font-semibold">
                                            {associate.name}
                                        </p>
                                    </td>
                                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                                        <p className="text-gray-900 whitespace-no-wrap font-mono text-xs">/associates/{associate.slug}</p>
                                    </td>
                                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-right whitespace-no-wrap">
                                        <Link to={`/admin/associate/edit/${associate.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4">
                                            Edit
                                        </Link>
                                        <button onClick={() => openDeleteModal(associate)} className="text-red-600 hover:text-red-900">
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                 {!isLoading && associates && associates.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        <p>No associates found.</p>
                        <p className="mt-2">
                            <Link to="/admin/associate/new" className="text-indigo-600 hover:underline">
                                Add your first one!
                            </Link>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminAssociatesPage;
